/**
 * Realtime WebSocket Client for PairUpToday
 *
 * Designed for 100% production reliability on Render, Cloud Run, and Local Dev:
 * - Dynamic wss:// vs ws:// detection (Render HTTPS auto-handling)
 * - Zero hardcoded localhost dependency (uses window.location.host)
 * - Heartbeat ping-pong to prevent Render 55s proxy timeout
 * - Exponential backoff reconnection with jitter
 * - Safe error suppression to eliminate Vite HMR console noise
 */

import { getWebSocketUrl } from './apiConfig';

type MessageHandler = (data: any) => void;

class RealtimeSocketClient {
  private socket: WebSocket | null = null;
  private listeners: Map<string, Set<MessageHandler>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimeout: any = null;
  private pingInterval: any = null;
  private isExplicitlyClosed = false;

  constructor() {
    // Only initialize in browser environment
    if (typeof window !== 'undefined') {
      this.connect();
    }
  }

  private getSocketUrl(): string {
    return getWebSocketUrl();
  }

  public connect() {
    if (typeof window === 'undefined') return;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.isExplicitlyClosed = false;
    const url = this.getSocketUrl();

    try {
      this.socket = new WebSocket(url);

      this.socket.onopen = () => {
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.dispatch('CONNECTED', { timestamp: Date.now() });
      };

      this.socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload && payload.type) {
            this.dispatch(payload.type, payload.data || payload);
          }
        } catch {
          // Ignore non-JSON system frames
        }
      };

      this.socket.onclose = (event) => {
        this.stopHeartbeat();
        this.socket = null;
        if (!this.isExplicitlyClosed) {
          this.scheduleReconnect();
        }
      };

      this.socket.onerror = () => {
        // Suppress noisy socket errors in dev/iframe, fallback to reconnect
        if (this.socket) {
          try {
            this.socket.close();
          } catch {
            // No-op
          }
        }
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    // Render proxies terminate idle connections at 55 seconds; ping every 25 seconds
    this.pingInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          this.socket.send(JSON.stringify({ type: 'PING', timestamp: Date.now() }));
        } catch {
          // Ignore
        }
      }
    }, 25000);
  }

  private stopHeartbeat() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // Stop aggressive retries to preserve battery/CPU; will retry on window focus
      return;
    }

    const backoff = Math.min(1000 * Math.pow(1.5, this.reconnectAttempts) + Math.random() * 500, 30000);
    this.reconnectAttempts++;

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect();
    }, backoff);
  }

  public subscribe(eventType: string, handler: MessageHandler): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(handler);

    return () => {
      const set = this.listeners.get(eventType);
      if (set) {
        set.delete(handler);
        if (set.size === 0) {
          this.listeners.delete(eventType);
        }
      }
    };
  }

  public send(type: string, data?: any) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify({ type, data, timestamp: Date.now() }));
      } catch {
        // Safe fail
      }
    }
  }

  private dispatch(eventType: string, data: any) {
    const handlers = this.listeners.get(eventType);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (e) {
          console.error(`Error in realtime handler for ${eventType}:`, e);
        }
      });
    }
  }

  public disconnect() {
    this.isExplicitlyClosed = true;
    this.stopHeartbeat();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.socket) {
      try {
        this.socket.close();
      } catch {
        // Safe
      }
      this.socket = null;
    }
  }
}

// Singleton instance across browser window
export const realtimeSocket = new RealtimeSocketClient();

// Reconnect on tab focus if disconnected
if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => {
    realtimeSocket.connect();
  });
}
